import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, Image, StyleSheet, View } from 'react-native';
import { useRecoilValue } from 'recoil';
import { SECOND } from 'time-constants';

import { lowerDisplayLineTestIDs, Theme } from '@constants';
import { useShowNextLDLProduct, useSunriseSunset } from '@hooks';
import { dayDateTimeState } from '@store';

import { ANIMATED_ICON_PATH_MAP } from '../../../assets/icons/animated/animated_icon_path_map';
import { TimeUtils } from '../../../helpers/TimeUtils';
import { TWCMoonIcon } from '../../primitives/TWCMoonIcon/TWCMoonIcon';
import { TWCText } from '../../primitives/TWCText/TWCText';

const testIDPath = lowerDisplayLineTestIDs.sunriseSunsetMoonPhase;
const tPath = 'lowerDisplayLine.sunriseSunsetMoonPhase.';
const sunriseSunsetIcon = ANIMATED_ICON_PATH_MAP.sunrisesunset;

const LDLSunriseSunsetAPI = () => {
  const data = useSunriseSunset();
  const showNextProduct = useShowNextLDLProduct();

  useEffect(() => {
    data.length === 0 && showNextProduct(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return data.length > 0 ? <LDLSunriseSunsetUI data={data} onFinish={showNextProduct} /> : <></>;
};

const LDLSunriseSunsetUI = ({ data, onFinish }) => {
  const { t } = useTranslation();
  const { timeZone } = useRecoilValue(dayDateTimeState);
  const { moonPhase, moonPhaseCode, sunriseTimeUtc, sunsetTimeUtc } = data[0];

  useEffect(() => {
    const animation = Animated.timing(new Animated.Value(0), {
      duration: 5 * SECOND,
      useNativeDriver: true,
    });

    animation.start(({ finished }) => finished && onFinish?.());
    return animation.reset;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const timeFormat = TimeUtils.getTimeFormat();
  const sunriseTime = TimeUtils.getZonedTime(TimeUtils.dateFromEpoch(sunriseTimeUtc), timeZone, timeFormat);
  const sunsetTime = TimeUtils.getZonedTime(TimeUtils.dateFromEpoch(sunsetTimeUtc), timeZone, timeFormat);
  const hasSunriseSunset = sunriseTime && sunsetTime;

  return (
    <>
      {hasSunriseSunset ? (
        <View style={styles.sunriseSunsetProduct}>
          <TWCText
            style={styles.productLabel}
            testID={testIDPath.sunriseSunsetProductLabel}
            variant={'condensedText36Transparent'}
          >
            {t(`${tPath}sunriseSunsetLabel`)}
          </TWCText>
          <TWCText style={styles.sunriseTime} testID={testIDPath.sunriseTime} variant={'condensedText36'}>
            {sunriseTime}
          </TWCText>
          <Image resizeMode={'contain'} source={sunriseSunsetIcon} style={styles.sunriseSunsetIcon} />
          <TWCText style={styles.sunsetTime} testID={testIDPath.sunsetTime} variant={'condensedText36'}>
            {sunsetTime}
          </TWCText>
        </View>
      ) : null}

      <View style={styles.moonPhaseProduct}>
        <TWCText
          style={styles.productLabel}
          testID={testIDPath.moonPhaseProductLabel}
          variant={'condensedText36Transparent'}
        >
          {t(`${tPath}moonPhaseLabel`)}
        </TWCText>
        <TWCMoonIcon code={moonPhaseCode} height={48} width={48} />
        <TWCText style={styles.moonPhaseValue} testID={testIDPath.moonPhaseValue} variant={'condensedText36'}>
          {moonPhase}
        </TWCText>
      </View>
    </>
  );
};

export const LDLSunriseSunset = {
  API: LDLSunriseSunsetAPI,
  UI: LDLSunriseSunsetUI,
};

const styles = StyleSheet.create({
  moonPhaseProduct: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  moonPhaseValue: {
    paddingLeft: Theme.px(10),
  },
  productLabel: {
    paddingRight: Theme.px(48),
  },
  sunriseSunsetIcon: {
    height: Theme.px(54),
    width: Theme.px(54),
  },
  sunriseSunsetProduct: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingRight: Theme.px(72),
  },
  sunriseTime: {
    paddingRight: Theme.px(10),
  },
  sunsetTime: {
    paddingLeft: Theme.px(10),
  },
});
